<template>
    <DCard
        :title="__('contracts.contract_upload.preview_title')"
        padding
    >
        <DFormRow
            :label="__('contracts.contract_upload.preview_table.affected_customers')"
            is-required
        >
            {{ customersCount }}
        </DFormRow>
        <DFormRow
            :label="__('contracts.contract_upload.preview_table.new_contracts')"
            is-required
        >
            {{ contractsCount }}
        </DFormRow>
        <DCard padding>
            <DRow
                align-horizontal="start"
                align-vertical="end"
                class="mb-3"
            >
                <DCol lg="2">
                    <DInput
                        id="customer_id"
                        v-model="filterData.customer_id"
                        :label="__('contracts.contract_upload.preview_table.customer')"
                        type="text"
                        pattern="^\d*$"
                        min="1"
                    />
                </DCol>
                <DCol lg="2">
                    <DInput
                        id="contract_number"
                        v-model="filterData.contract_number"
                        :label="__('contracts.contract_upload.preview_table.contract')"
                        type="text"
                        pattern="^\d*$"
                        min="1"
                    />
                </DCol>
                <DCol
                    lg="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="changePage"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
        </DCard>
        <DRow>
            <DCol>
                <DTable
                    :items="items"
                    :fields="fields"
                    hover
                >
                    <template #customer="{item}">
                        <td>
                            {{ item.customer.id }}
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="paginationData.last_page >= 1"
                    :active-page="paginationData.current_page"
                    :pages="paginationData.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
            </DCol>
        </DRow>
    </DCard>
    <DFormControls
        :disabled="disabledActions"
        :cancel-route="cancelRoute"
        :submit-message="__('contracts.contract_upload.messages.apply')"
        submit-icon="check"
        @submit="submitForm"
    >
        <template #additional_button_before_submit>
            <DFab
                id="btn-draft"
                :disabled="disabledActions"
                :icon="'content-save'"
                :label="__('contracts.contract_upload.messages.save_as_draft')"
                @click="submitForm('draft')"
            />
        </template>
    </DFormControls>
</template>

<script setup>
import { computed, ref } from 'vue'
import { route } from '~@/Composables/route'
import { router } from '@inertiajs/vue3'
import { __ } from '~@/Composables/translation'

const props = defineProps({
    customersCount: {
        type: Number,
        default: null
    },
    contractsCount: {
        type: Number,
        default: null
    },
    contractCsvFile: {
        type: Object,
        default: () => {
            return {}
        }
    },
    items: {
        type: Object,
        default: () => {
            return {}
        }
    },
    paginationData: {
        type: Object,
        default: () => {
            return {}
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    }
})

const fields = [
    { key: 'id', label: __('contracts.contract_upload.headers.id') },
    { key: 'customer', label: __('contracts.contract_upload.headers.customer') },
    { key: 'contract', label: __('contracts.contract_upload.headers.contract') },
    { key: 'product', label: __('contracts.contract_upload.headers.product') },
    { key: 'product_plan_id', label: __('contracts.contract_upload.headers.product_plan_id') },
    { key: 'addon_product1_id', label: __('contracts.contract_upload.headers.addon_product1_id') },
    { key: 'addon_product1_plan_id', label: __('contracts.contract_upload.headers.addon_product1_plan_id') },
    { key: 'addon_product2_id', label: __('contracts.contract_upload.headers.addon_product2_id') },
    { key: 'addon_product2_plan_id', label: __('contracts.contract_upload.headers.addon_product2_plan_id') },
    { key: 'free_months', label: __('contracts.contract_upload.headers.free_months') },
    { key: 'internal_note', label: __('contracts.contract_upload.headers.internal_note') }
]

const filterData = ref(props.filters)

const disabledActions = computed(() => props.contractsCount < 1)

const cancelRoute = computed(() => {
    return route('contracts.contract-upload.cancel', props.contractCsvFile.id)
})
const submitForm = (status) => router.get(route('contracts.contract-upload.submit', { contractCsvFile: props.contractCsvFile, status: status }))

const changePage = (page) => {
    const query = Object.fromEntries(Object.entries(filterData.value).filter(([_, v]) => v))
    if (page > 1) {
        query.page = page
    }

    router.get(route('contracts.contract-upload.preview', { contractCsvFile: props.contractCsvFile.id }), query)
}

const resetFilters = () => {
    filterData.value.contract_number = null
    filterData.value.customer_id = null
    changePage()
}
</script>
